import classNames from 'classnames';
import { addDays, formatISO, isAfter, isBefore, isSameDay, Locale, subDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange as DateRangePickerComponent, DateRangeProps, Range, RangeKeyDict } from 'react-date-range';
import { DefaultRootState, useSelector } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { useOnClickOutside } from 'usehooks-ts';

import { useBowser, useI18n } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import Button from '../Button/Button.component';
import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import { Tooltip } from '../Tooltip';
import { TooltipAlign, TooltipPlacementType, TooltipTriggerType } from '../Tooltip/Tooltip.component';
import './index.css';
import { getFormattedRange, getLocaleAsync } from './utils';

const KEY = 'range';

const DATE_OF_STARTUP_PRODUCT = new Date(formatISO(new Date('2022-09-01T00:00:00'))); // start of a collecting data (startup of the product)
const TODAY_DATE = new Date(formatISO(new Date()));
const MAX_DAYS_PERSIST_DATA = 91;

export type DateRangePickerValue = Required<Pick<Range, 'startDate' | 'endDate' | 'key'>>;

type Props = {
  onChange?: (range: DateRangePickerValue | undefined) => void;
  onReset?: () => DateRangePickerValue | undefined;
  value?: DateRangePickerValue | undefined;
  children?: React.ReactElement;
  placement?: TooltipPlacementType;
  maxDays?: number | null;
  align?: TooltipAlign;
  tooltip?: string;
};

const initialRange: DateRangePickerValue = {
  startDate: TODAY_DATE,
  endDate: TODAY_DATE,
  key: KEY,
};

export const DateRangePicker: React.FC<React.PropsWithChildren<Props>> = React.memo(
  ({
    children,
    value,
    onChange,
    align,
    placement = 'bottomLeft',
    onReset,
    tooltip,
    maxDays = MAX_DAYS_PERSIST_DATA,
  }) => {
    const { isMobile, isIOS } = useBowser();
    const { translateToText } = useI18n();
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    const [touched, setTouched] = useState(false);

    const [range, setRange] = useState<DateRangePickerValue | undefined>(value);
    const [tempRange, setTempRange] = useState<DateRangePickerValue | undefined>(value);
    const [userSelectionRange, setUserSelectionRange] = useState<DateRangePickerValue | undefined>();

    const [locale, setLocale] = useState<Locale | undefined>();
    const culture = useSelector((state: DefaultRootState) => state.common.resources.culture);

    useOnClickOutside(ref, () => setVisible(!visible));

    useEffect(() => {
      getLocaleAsync(setLocale);
    }, [culture]);

    useEffect(
      function updateRangeFromProps() {
        setRange(value);
        setTempRange(value);
      },
      [value],
    );

    const handleSelect = useCallback(
      (params: RangeKeyDict) => {
        const _range = params[value?.key || KEY];
        setTouched(true);
        setUserSelectionRange({
          startDate: _range?.startDate ?? initialRange.startDate,
          endDate: _range?.endDate ?? initialRange.startDate,
          key: _range?.key ?? KEY,
        });
        setTempRange({
          startDate: _range?.startDate ?? initialRange.startDate,
          endDate: _range?.endDate ?? initialRange.startDate,
          key: _range?.key ?? KEY,
        });
      },
      [value?.key],
    );

    const onComplete = useCallback(() => {
      setVisible(!visible);
      setTouched(false);
      // if (tempRange?.startDate && tempRange.endDate && tempRange.key) {
      //   onChange?.(tempRange);
      // }
      onChange?.(tempRange);
    }, [onChange, tempRange, visible]);

    const onCancel = useCallback(() => {
      setVisible(!visible);
      setTempRange(range);
      setUserSelectionRange(undefined);
      setTouched(false);
    }, [range, visible]);

    useEffect(() => {
      setTempRange(value);
    }, [value]);

    const onResetDate = useCallback(() => {
      // if (onReset) {
      //   const initDataRange = onReset();
      //   setTempRange(initDataRange);
      //   setTouched(false);
      // } else {
      //   setTempRange(undefined);
      //   setTouched(false);
      //   onChange?.(undefined);
      // }

      if (onReset) {
        const initDataRange = onReset();
        setTempRange(initDataRange);
        setUserSelectionRange(undefined);
        setTouched(true);
      } else {
        setTempRange(undefined);
        setUserSelectionRange(undefined);
        setTouched(true);
      }
    }, [onReset]);

    const onVisibleChange = useCallback(
      (isVisible?: boolean) => {
        if (!isVisible) setTempRange(value);
      },
      [value],
    );

    const iconClassNames = classNames({
      'button--circle-active': visible,
      'button--circle-indicator': range !== undefined,
    });

    const allMonthsAreAllowedToChoose = useMemo(
      () =>
        // if startDate === endDate - user didn't pick a second date for the range
        tempRange === undefined || !isSameDay(tempRange.startDate, tempRange.endDate),
      [tempRange],
    );

    const getRangeText = useMemo(
      () => getFormattedRange(locale, tempRange?.startDate, tempRange?.endDate),
      [locale, tempRange?.endDate, tempRange?.startDate],
    );

    const disabledDayCallback: DateRangeProps['disabledDay'] = useCallback(
      (day: Date) => {
        if (allMonthsAreAllowedToChoose || !maxDays) return false;

        const targetDate = userSelectionRange?.startDate;

        if (!targetDate) return false;

        const maxDate = addDays(targetDate, maxDays);
        const minDate = subDays(targetDate, maxDays);

        return isBefore(day, minDate) || isAfter(day, maxDate);
      },
      [allMonthsAreAllowedToChoose, maxDays, userSelectionRange?.startDate],
    );

    const renderDateRangePicker = useMemo(
      () => (
        <DateRangePickerComponent
          ranges={[tempRange ?? initialRange]}
          onChange={handleSelect}
          scroll={{
            enabled: true,
            monthWidth: 340,
            monthHeight: isMobile ? 260 : 345,
          }}
          shownDate={tempRange?.startDate}
          showMonthAndYearPickers={false}
          showDateDisplay={false}
          preventSnapRefocus={false}
          moveRangeOnFirstSelection={false}
          direction={isMobile ? 'vertical' : 'horizontal'}
          months={2}
          disabledDay={disabledDayCallback}
          monthDisplayFormat='MMMM yyyy'
          minDate={DATE_OF_STARTUP_PRODUCT}
          maxDate={TODAY_DATE}
          locale={locale}
          rangeColors={[isMobile ? '#4F2DD0' : '#140533']}
          color='#140533'
        />
      ),
      [disabledDayCallback, handleSelect, isMobile, locale, tempRange],
    );

    const renderTitle = useMemo(
      () => (
        <>
          <p className='date-range__title H1_med_20'>
            {translateToText('mobile:wallet:transactions-history:daterange:title')}
          </p>
          <button type='button' className='date-range__reset' onClick={onResetDate}>
            {translateToText('mobile:wallet:transactions-history:daterange:reset-btn')}
          </button>
        </>
      ),
      [onResetDate, translateToText],
    );

    const renderControls = useMemo(
      () => (
        <div className='date-range__btn flex-row gap-16'>
          <Button outline large onClick={onCancel}>
            {translateToText('mobile:wallet:transactions-history:daterange:cancel-btn')}
          </Button>
          <Button large inactive={!touched} onClick={onComplete}>
            <p>{translateToText('mobile:wallet:transactions-history:daterange:confirm-btn')}</p>
            {getRangeText.length > 0 && <span>{getRangeText}</span>}
          </Button>
        </div>
      ),
      [onCancel, translateToText, onComplete, getRangeText, touched],
    );

    const renderIcon = useMemo(() => {
      if (children)
        return React.cloneElement(children, {
          onClick: () => setVisible(!visible),
        });

      return (
        <Tooltip enabled={!!tooltip && !visible} overlay={tooltip ?? ''} placement='bottom'>
          <Button circle onClick={() => setVisible(!visible)} className={iconClassNames}>
            <SvgIconUrl symbol={svgIcons.Calendar()} />
          </Button>
        </Tooltip>
      );
    }, [children, tooltip, iconClassNames, visible]);

    const trigger: TooltipTriggerType = useMemo(() => ['click'], []);

    return isMobile ? (
      <>
        {renderIcon}
        <BottomSheet
          className='light'
          initialFocusRef={false}
          expandOnContentDrag={false}
          scrollLocking={!isIOS}
          blocking
          header={renderTitle}
          snapPoints={({ minHeight }) => [minHeight]}
          defaultSnap={({ snapPoints }) => Math.min(...snapPoints)}
          onDismiss={() => setVisible(false)}
          open={visible}
          footer={renderControls}
        >
          {renderDateRangePicker}
        </BottomSheet>
      </>
    ) : (
      <Tooltip
        visible={visible}
        overlayClassName='date-range light'
        enabled
        align={align}
        onVisibleChange={onVisibleChange}
        overlay={
          <div ref={ref}>
            {renderTitle}
            {renderDateRangePicker}
            {renderControls}
          </div>
        }
        trigger={trigger}
        placement={placement}
      >
        {renderIcon}
      </Tooltip>
    );
  },
);
